export type SequenceMode = 'arithmetic' | 'geometric';

export interface SequenceVariableLabels {
  /** Symbol for each term, rendered as u_n, a_n, T_n, etc. */
  termSymbol?: string;
  /** Symbol for the index variable */
  indexSymbol?: string;
  /** Label for the first term slider/display */
  firstTermLabel?: string;
  /** Label for the common difference/ratio slider/display */
  commonFactorLabel?: string;
  /** Symbol for the series partial sum */
  seriesSymbol?: string;
}

export interface SequenceSeriesRange {
  /** Starting term index (1-based) */
  from: number;
  /** Ending term index (max 300) */
  to: number;
}

interface BaseSequenceSeriesParams {
  firstTerm: number;
  commonFactor: number;
  showSeries: boolean;
  range: SequenceSeriesRange;
  labels?: SequenceVariableLabels;
  showSliders?: boolean;
  /** Show numeric value on top of each bar / line point */
  showValues?: boolean;
  /** Template for x-axis labels, e.g. "Year {n}". {n} is replaced with the term index. When unset, uses termSymbol_n (e.g. u₁). */
  xAxisLabelTemplate?: string;
  /** Prefix prepended to displayed values, e.g. "$" */
  valuePrefix?: string;
  /** Suffix appended to displayed values, e.g. "%" */
  valueSuffix?: string;
  /** Force a data zoom slider even when the term count is ≤ 50 */
  forceDataZoom?: boolean;
}

export interface ArithmeticParams extends BaseSequenceSeriesParams {
  mode: 'arithmetic';
  /** When true, first bar is pink (a) with +d arrows between consecutive bars */
  explainerMode?: boolean;
}

export interface GeometricParams extends BaseSequenceSeriesParams {
  mode: 'geometric';
  /** When true, first bar is pink (a) with ×r arrows between consecutive bars */
  explainerMode?: boolean;
  /** Show horizontal S∞ = a/(1-r) convergence line when |r| < 1 */
  showSumToInfinity?: boolean;
}

export type SequenceSeriesParams = ArithmeticParams | GeometricParams;

export interface SequenceSeriesSliderConfig {
  firstTerm: { min: number; max: number; step: number };
  commonFactor: { min: number; max: number; step: number };
}

export const MAX_TERM_COUNT = 300;

export const DEFAULT_SEQUENCE_SERIES_PARAMS: ArithmeticParams = {
  mode: 'arithmetic',
  firstTerm: 2,
  commonFactor: 3,
  showSeries: false,
  range: { from: 1, to: 10 },
  showSliders: false,
};

export const DEFAULT_SLIDER_CONFIG: SequenceSeriesSliderConfig = {
  firstTerm: { min: -20, max: 20, step: 0.5 },
  commonFactor: { min: -10, max: 10, step: 0.5 },
};

export const DEFAULT_ARITHMETIC_LABELS: Required<SequenceVariableLabels> = {
  termSymbol: 'u',
  indexSymbol: 'n',
  firstTermLabel: 'a',
  commonFactorLabel: 'd',
  seriesSymbol: 'S',
};

export const DEFAULT_GEOMETRIC_LABELS: Required<SequenceVariableLabels> = {
  termSymbol: 'u',
  indexSymbol: 'n',
  firstTermLabel: 'a',
  commonFactorLabel: 'r',
  seriesSymbol: 'S',
};
